import './Landing.css';
import prspLogo from './prsp-logo.png';

import CircularProgress from '@mui/material/CircularProgress';

import { Link, Navigate } from 'react-router-dom';

import { useAuth } from '../../AuthContext';

export const Landing = () => {
    const { auth, loading } = useAuth();

    if (loading) {
        return <CircularProgress />;
    }

    // Logged-in users never see the marketing page — mirror the existing
    // LogInWrapper behavior: verified users go to the dashboard, unverified
    // users go to the help/verification page.
    if (auth.currentUser) {
        return <Navigate to={ auth.currentUser.emailVerified ? '/dashboard' : '/help' } replace />;
    }

    return (
        <div className="swap-landing">
            <header className="hero">
                <img className="logo" src={ prspLogo } alt="Powder Ridge Ski Patrol" />
                <h1>Annual Ski &amp;<br />Snowboard Swap</h1>
                <div className="tag">Get your gear ready — clean out the garage and cash in.</div>
                <div className="when">Drop-off &amp; sale: early November</div>
                <br />
                <Link className="btn" to="/register">Pre-register &amp; list your gear</Link>
                <span className="login-link">Already registered? <Link to="/login">Log in</Link></span>
            </header>

            <section>
                <div className="wrap">
                    <h2>Why now?</h2>
                    <p className="lead">Cleaning out the garage this summer? Set aside the snow gear you no longer use.</p>
                    <p className="intro-copy">
                        The Powder Ridge Ski Patrol Swap is your chance to turn old skis, boards, and boots into cash —
                        and help your neighbors gear up for winter. Pre-register now so your items are ready to sell when the swap opens.
                    </p>
                </div>
            </section>

            <section className="alt">
                <div className="wrap">
                    <h2>How it works</h2>
                    <p className="lead">Three steps from garage to sold.</p>
                    <div className="steps">
                        <div className="step">
                            <div className="num">1</div>
                            <div><h3>Pre-register online</h3><p>Create your free seller account in a couple of minutes.</p></div>
                        </div>
                        <div className="step">
                            <div className="num">2</div>
                            <div><h3>List your gear early</h3><p>Add each item with a photo, size, and your asking price — just $2 per item to list.</p></div>
                        </div>
                        <div className="step">
                            <div className="num">3</div>
                            <div><h3>Drop off &amp; get paid</h3><p>Bring your items to the swap in early November. We sell them — you collect your earnings.</p></div>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <div className="wrap">
                    <h2>What can you sell?</h2>
                    <p className="lead">If it&rsquo;s snow gear in good shape, bring it.</p>
                    <div className="chips">
                        <span className="chip">Skis</span><span className="chip">Snowboards</span><span className="chip">Boots</span>
                        <span className="chip">Bindings</span><span className="chip">Poles</span><span className="chip">Helmets</span>
                        <span className="chip">Jackets &amp; pants</span><span className="chip">Goggles</span><span className="chip">Kids&rsquo; gear</span>
                    </div>
                </div>
            </section>

            <section className="alt">
                <div className="wrap">
                    <h2>Fees</h2>
                    <p className="lead">Simple and upfront.</p>
                    <div className="fees">
                        <div className="fee">
                            <div className="amt">$2 / item</div>
                            <p><b>Listing fee</b>A flat $2 per item to get it into the swap, paid when you list. You pay this whether or not the item sells.</p>
                        </div>
                        <div className="fee">
                            <div className="amt">10%</div>
                            <p><b>When it sells</b>10% of each sale goes to Powder Ridge Ski Patrol. You keep the rest.</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="proceeds">
                <p>All proceeds support Powder Ridge Ski Patrol — funding equipment, training, and the volunteers who keep the mountain safe.</p>
                <div className="small">Thank you for supporting your local ski patrol.</div>
            </div>

            <section className="alt">
                <div className="wrap">
                    <h2>Good to know</h2>
                    <p className="lead">A few quick answers.</p>
                    <div className="faq">
                        <details><summary>When is the swap?</summary><p>Drop-off and the sale happen in early November. Exact dates and times will be posted here and emailed to registered sellers.</p></details>
                        <details><summary>Can I register now if the swap is months away?</summary><p>Yes — that&rsquo;s the idea. Pre-register and list your gear over the summer so everything&rsquo;s ready to go. The earlier you list, the less there is to do in November.</p></details>
                        <details><summary>How do I get paid?</summary><p>After the swap, your earnings from sold items are paid out to you. Unsold items are returned at pickup.</p></details>
                        <details><summary>How do I pay the listing fee?</summary><p>You can pay your $2-per-item listing fee online at checkout, or in cash when you drop off your items.</p></details>
                        <details><summary>What condition does gear need to be in?</summary><p>Clean, safe, and in good working order. Items should be ready for a new owner to use this season.</p></details>
                    </div>
                </div>
            </section>

            <section className="final">
                <div className="wrap">
                    <h2>Ready?</h2>
                    <p className="lead">Pre-register and start listing your gear.</p>
                    <p style={ { color: 'var(--muted)', margin: '0 0 8px' } }>It only takes a couple of minutes.</p>
                    <Link className="btn" to="/register">Create your seller account</Link>
                    <span className="login-link">Already have an account? <Link to="/login">Log in</Link></span>
                </div>
            </section>

            <footer className="lp-footer">
                <div className="brand">Powder Ridge Ski Patrol — Annual Ski &amp; Snowboard Swap</div>
            </footer>
        </div>
    );
};
